import React, { useContext, useRef, useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  IconButton,
  Tooltip,
  DialogContent,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { ClipLoader } from 'react-spinners';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { AppInfoContext } from '../../../../../App';
import { useWaterBalances } from '../hooks/useWaterBalances';
import { ReactComponent as ExportIcon } from '../images/exportExcel.svg';
import WaterLossBarChart from './WaterLossBarChart';
import LitersPerConnectionChart from './LitersPerConnectionChart';
import M3PerKmPerHourChart from './M3PerKmPerHourChart';
import ExportDiagramButton from '../../watermanagement/ExportDiagramButton';
import { exportWaterBalance } from './exportWaterBalance';
import FullScreenDiagram from '../../watermanagement/FullScreenDiagram';

const WaterBalanceContent = () => {
  const { appInformation } = useContext(AppInfoContext);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenChart, setFullscreenChart] = useState(null);
  const {
    waterBalance,
    isLoadingWaterBalance,
    error: waterBalanceError,
    refetch: refetchWaterBalance,
  } = useWaterBalances(appInformation.waterManagement?.waterBalanceData?.dmaId);
  
  const monthly = waterBalance?.monthly ?? [];
  const zoneName = appInformation.waterManagement?.waterBalanceData?.dmaName ?? 'Cijeli Sistem';
  const nrwChartRef = useRef();
  const litersChartRef = useRef();
  const m3ChartRef = useRef();

  const openFullscreen = (title, Chart) => {
    setFullscreenChart({ title, Chart });
    setFullscreenOpen(true);
  };

  return (
    <DialogContent
      dividers
      sx={{
        p: { xs: 1, sm: 2 },
        minWidth: 0,
        backgroundColor: '#f2f2f2',
      }}
    >
      {isLoadingWaterBalance ? (
        <Box className="waterbalance-modal-loader">
          <ClipLoader color="#21538f" loading={isLoadingWaterBalance} size={70} data-testid="loader" />
        </Box>
      ) : waterBalanceError ? (
        <Box className="waterbalance-modal-error">
          <Typography sx={{ fontSize: 14, color: '#717171' }}>Podaci nisu učitani.</Typography>
          <Button
            size="small"
            onClick={() => refetchWaterBalance()}
            sx={{
              border: '1px solid #21538f',
              borderRadius: 0,
              textTransform: 'none',
              color: '#21538f',
              px: 2,
            }}
          >
            Pokušaj ponovo
          </Button>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <Grid2 container spacing={2} alignItems="stretch">
            <Grid2 xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 0,
                  border: '1px solid #e2e6e2',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: 'auto',
                }}
              >
                <Box className="waterbalance-summary-head">
                  <Typography
                    sx={{
                      fontSize: { xs: 14, sm: 16 },
                      fontWeight: 600,
                      color: '#717171',
                      letterSpacing: '1.5px',
                      width: { xs: '100%', sm: 'auto' },
                      textAlign: { xs: 'center', sm: 'left' },
                    }}
                  >
                    VODOVODNI SISTEM GRAČANICA
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      width: { xs: '100%', sm: 'auto' },
                      gap: 1.5,
                    }}
                  >
                    <Button
                      size="small"
                      startIcon={<ExportIcon sx={{ fontSize: 18 }} />}
                      onClick={() => exportWaterBalance(waterBalance, zoneName)}
                      disabled={!waterBalance}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 0,
                        textTransform: "none",
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        fontWeight: 500,
                        height: 32,
                        px: { xs: 1, sm: 2 },
                        flex: { xs: 1, sm: 'none' },
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        color: "#555",
                        backgroundColor: "#fafafa",
                        "&:hover": { backgroundColor: "#f2f2f2" },
                      }}
                    >
                      Eksportuj
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 0,
                        textTransform: "none",
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        fontWeight: 500,
                        height: 32,
                        px: { xs: 1, sm: 2 },
                        flex: { xs: 1, sm: 'none' },
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        color: "#555",
                        backgroundColor: "#fafafa",
                        "&:hover": { backgroundColor: "#f2f2f2" },
                      }}
                    >
                      Odaberi mjesec
                    </Button>
                  </Box>
                </Box>
                <Box className="waterbalance-summary-row">
                  <Box className="waterbalance-summary-stats">
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>DMA zona</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>{appInformation.waterManagement?.waterBalanceData?.dmaName ?? 'Cijeli Sistem'}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>Potrošači</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>
                        {waterBalance?.consumers?.toLocaleString()}
                      </Typography>
                    </Box>

                    {/* UARL is based on service connections, not consumers */}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>Priključci</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>
                        {waterBalance?.serviceConnections?.toLocaleString()}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>Dužina mreže</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>{waterBalance?.distributionPipesLength}km</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>Priključni cjevovod</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>{waterBalance?.connectionPipesLength}km</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>Prosječni pritisak</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>{waterBalance?.pressureBar} bar</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontSize: 13, color: '#b0b0b0' }}>Period</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>{waterBalance?.period} god.</Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid2>
            <Grid2 xs={12} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 0,
                  border: '1px solid #e2e6e2',
                  backgroundColor: '#fff',
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(4, minmax(0, 1fr))',
                    md: '1fr',
                  },
                  alignContent: 'start',
                  columnGap: 2,
                  rowGap: 1,
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#717171',
                    }}
                  >
                    NRW (%)
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: '#e5533d',
                      lineHeight: 1.1,
                    }}
                  >
                    {waterBalance?.nrwPercent == null
                      ? '—'
                      : `${waterBalance.nrwPercent.toFixed(1)}%`}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#717171',
                    }}
                  >
                    ILI
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 26,
                      fontWeight: 700,
                      color: '#f59e0b',
                      lineHeight: 1.1,
                    }}
                  >
                    {waterBalance?.ili == null ? '—' : waterBalance.ili.toFixed(1)}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#717171',
                    }}
                  >
                    CARL
                  </Typography>

                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>
                    {waterBalance?.carl?.toLocaleString()} m³/god
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: '#717171',
                    }}
                  >
                    UARL
                  </Typography>

                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#717171' }}>
                    {waterBalance?.uarl?.toLocaleString()} m³/god
                  </Typography>
                </Box>
              </Paper>
            </Grid2>

            <Grid2 xs={12} md={9}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 0,
                  border: '1px solid #e2e6e2',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{color: '#717171'}}>
                      Prikaz po mjesecima (NRW - ILI)
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExportDiagramButton
                      chartRef={nrwChartRef}
                      chartName={`Bilans-NRW-ILI-${zoneName}-${waterBalance?.period}`}
                    />
                    <Tooltip title="Uvećaj ekran">
                      <IconButton
                        size="small"
                        sx={{ color: "#21538f" }}
                        onClick={() => openFullscreen('Prikaz po mjesecima (NRW - ILI)', WaterLossBarChart)}
                      >
                        <FullscreenIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box className="waterbalance-chart-area">
                  <WaterLossBarChart ref={nrwChartRef} monthly={monthly} />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 0,
                  border: '1px solid #e2e6e2',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{color: '#717171'}}>
                      Prikaz l/priključku na dan
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExportDiagramButton
                      chartRef={litersChartRef}
                      chartName={`Bilans-L-po-prikljucku-dan-${zoneName}-${waterBalance?.period}`}
                    />
                    <Tooltip title="Uvećaj ekran">
                      <IconButton
                        size="small"
                        sx={{ color: "#21538f" }}
                        onClick={() => openFullscreen('Prikaz l/priključku na dan', LitersPerConnectionChart)}
                      >
                        <FullscreenIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box className="waterbalance-chart-area">
                  <LitersPerConnectionChart ref={litersChartRef} monthly={monthly} />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 0,
                  border: '1px solid #e2e6e2',
                  backgroundColor: '#fff',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{color: '#717171'}}>
                      Prikaz m³/km cjevovoda na sat
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ExportDiagramButton
                      chartRef={m3ChartRef}
                      chartName={`Bilans-m3-po-km-sat-${zoneName}-${waterBalance?.period}`}
                    />
                    <Tooltip title="Uvećaj ekran">
                      <IconButton
                        size="small"
                        sx={{ color: "#21538f" }}
                        onClick={() => openFullscreen('Prikaz m³/km cjevovoda na sat', M3PerKmPerHourChart)}
                      >
                        <FullscreenIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box className="waterbalance-chart-area">
                  <M3PerKmPerHourChart ref={m3ChartRef} monthly={monthly} />
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        </Box>
      )}
      <FullScreenDiagram
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        chartType={fullscreenChart?.title}
        period={`${zoneName}, ${waterBalance?.period} god.`}
      >
        <Box sx={{ height: '100%', '& .MuiBox-root': { height: '100%' } }}>
          {fullscreenChart && <fullscreenChart.Chart monthly={monthly} />}
        </Box>
      </FullScreenDiagram>
    </DialogContent>
  );
};

export default WaterBalanceContent;
